import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Printer } from 'lucide-react';
import InvoiceA4Document from '../components/Invoice/InvoiceA4Document';
import { invoiceToDocument } from '../utils/invoiceToDocument';

const apiUrl = process.env.REACT_APP_API_URL;

// Standalone, print-friendly HTML rendering of an invoice.
// Opened in a new tab from the invoice view page. Unlike the PDF preview this
// needs no stored file - it renders straight from the invoice data.
const InvoiceHtmlPreviewPage = () => {
  const { id: invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [businessDetails, setBusinessDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError('');

        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Please log in to view this invoice.');
        }

        const headers = { Authorization: `Bearer ${token}` };

        const response = await fetch(`${apiUrl}/invoice-view/${invoiceId}`, { headers });
        if (!response.ok) {
          throw new Error(`Failed to load invoice: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        setInvoice(data.invoice);
        setItems(data.items || []);

        // Business details are optional - the document still renders without
        // them, just with blank company info.
        try {
          const settingsResponse = await fetch(`${apiUrl}/business-settings`, { headers });
          if (settingsResponse.ok) {
            setBusinessDetails((await settingsResponse.json()) || {});
          }
        } catch (settingsError) {
          console.error('Error fetching business settings:', settingsError);
        }
      } catch (err) {
        console.error('Error loading invoice preview:', err);
        setError(err.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    if (invoiceId) {
      fetchInvoice();
    }
  }, [invoiceId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Preview unavailable</h2>
          <p className="text-gray-600">{error || 'Invoice not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8 print:bg-white print:py-0">
      {/* Toolbar - hidden when printing */}
      <div className="mb-4 flex justify-center print:hidden">
        <div style={{ width: '210mm' }} className="flex justify-end px-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-indigo-600 text-white hover:bg-indigo-700 rounded-md transition-colors"
          >
            <Printer size={16} />
            Печати
          </button>
        </div>
      </div>

      {/* The sheet keeps its fixed 210mm width so it prints correctly; on a
          narrow screen it scrolls sideways instead of shrinking. */}
      <div className="overflow-x-auto print:overflow-visible">
        <InvoiceA4Document
          invoice={invoiceToDocument(invoice, items)}
          businessDetails={businessDetails}
          documentType="invoice"
          invoiceType="outgoing"
        />
      </div>
    </div>
  );
};

export default InvoiceHtmlPreviewPage;
